// Shared "safe delete" dialog for analytics objects (connection / business
// area / visual schema / visual / dashboard). On open it asks the backend
// which other objects still reference this one:
//   • any dependents  → the delete is BLOCKED; they're listed and the only
//     action is Close (remove/repoint them first).
//   • none            → a plain "Delete? — can't be undone" confirm.
// The backend enforces the same rule (a referenced delete 409s), so a race
// between the check and the delete still surfaces the dependents here.
import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { AlertTriangle, Ban } from 'lucide-react'
import Modal from '@/components/common/Modal'
import {
  objectDependents,
  type DependableType,
  type ObjectDependent,
} from '@/api/designers'
import { errMessage } from './shared'
import { DependentList } from './WhereUsedDialog'

export default function DeleteObjectDialog({
  open,
  project,
  objType,
  objName,
  objLabel,
  displayName,
  onClose,
  onDeleted,
  doDelete,
}: {
  open: boolean
  project: string
  objType: DependableType
  objName: string
  /** Lowercase noun for prose, e.g. "visual", "dashboard", "business area". */
  objLabel: string
  /** Friendly title to show (defaults to objName). */
  displayName?: string
  onClose: () => void
  /** Called after a successful delete (section should refresh its list). */
  onDeleted: () => void
  /** Performs the actual delete (section supplies the right API call). */
  doDelete: () => Promise<void>
}) {
  const [loading, setLoading] = useState(true)
  const [deps, setDeps] = useState<ObjectDependent[] | null>(null)
  const [checkError, setCheckError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setLoading(true)
    setCheckError(null)
    setDeps(null)
    objectDependents(project, objType, objName)
      .then((r) => { if (!cancelled) setDeps(r.dependents || []) })
      .catch((e) => { if (!cancelled) setCheckError(errMessage(e, 'Could not check where this is used')) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [open, project, objType, objName])

  const title = displayName || objName
  const blocked = (deps?.length ?? 0) > 0

  const runDelete = async () => {
    setBusy(true)
    try {
      await doDelete()
      toast.success(`Deleted ${objLabel} “${title}”`)
      onDeleted()
      onClose()
    } catch (e) {
      // Race: something started referencing it between the check and the
      // delete — the backend 409 carries the fresh dependent list.
      const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
      const list = detail && typeof detail === 'object'
        ? (detail as { dependents?: ObjectDependent[] }).dependents
        : undefined
      if (Array.isArray(list) && list.length > 0) {
        setDeps(list)
        toast.error('Delete blocked — this is now in use')
      } else {
        toast.error(errMessage(e, `Could not delete the ${objLabel}`))
      }
    } finally {
      setBusy(false)
    }
  }

  const footer = blocked ? (
    <button className="btn btn-secondary" onClick={onClose}>Close</button>
  ) : (
    <>
      <button className="btn btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
      <button
        className="btn btn-danger"
        onClick={() => void runDelete()}
        disabled={busy || loading || !!checkError}
      >
        {busy ? 'Deleting…' : `Delete ${objLabel}`}
      </button>
    </>
  )

  return (
    <Modal
      open={open}
      onClose={() => { if (!busy) onClose() }}
      title={blocked ? `Can’t delete this ${objLabel}` : `Delete ${objLabel}?`}
      footer={footer}
    >
      {loading ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 15, color: 'var(--color-text-secondary)', padding: '6px 2px' }}>
          <div className="spinner" style={{ width: 15, height: 15 }} />
          Checking where “{title}” is used…
        </div>
      ) : checkError ? (
        <div
          style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
            borderRadius: 8, border: '1px solid var(--color-error)',
            background: 'var(--color-error-weak)', color: 'var(--color-error)', fontSize: 15,
          }}
        >
          {checkError} — try again.
        </div>
      ) : blocked ? (
        <div style={{ display: 'grid', gap: 12 }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <Ban size={18} style={{ color: 'var(--color-error)', flexShrink: 0, marginTop: 1 }} />
            <div style={{ fontSize: 15, lineHeight: 1.55 }}>
              <strong>“{title}”</strong> is used by {deps!.length} other{' '}
              object{deps!.length === 1 ? '' : 's'}. Remove or repoint{' '}
              {deps!.length === 1 ? 'it' : 'them'} first, then you can delete this {objLabel}.
            </div>
          </div>
          <DependentList deps={deps!} />
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 15, lineHeight: 1.55 }}>
          <AlertTriangle size={18} style={{ color: 'var(--color-warning)', flexShrink: 0, marginTop: 1 }} />
          <div>
            This permanently deletes the {objLabel} <strong>“{title}”</strong>.
            Nothing else references it, so it’s safe to remove — but this can’t be undone.
          </div>
        </div>
      )}
    </Modal>
  )
}
